import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faSearch, faSpinner, faQuestionCircle, faCheckCircle,
  faInfoCircle, faRobot, faEdit, faExternalLinkAlt, faLanguage, faGlobe,
  faRedo, faSave, faTimes, faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface Question {
  id: number;
  exam_id: number;
  question_text: string;
  options: { [key: string]: string };
  correct_answer_keys?: string[];
  explanation?: string;
  is_free_tier: boolean;
  topic?: string;
  difficulty?: number;
  created_at: string;
  ai_generated_explanation?: boolean;
}

interface Exam {
  id: number;
  name: string;
  description?: string;
}

interface Translation {
  id: number;
  language_code: string;
  language_name?: string;
  native_name?: string;
  question_text: string;
  options: { [key: string]: string };
  explanation?: string;
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
}

const SUPPORTED_LANGUAGES = [
  { code: 'zh-CN', name: 'Chinese Simplified', native: '简体中文' },
  { code: 'zh-TW', name: 'Chinese Traditional', native: '繁體中文' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'id', name: 'Indonesian', native: 'Bahasa Indonesia' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'pt-BR', name: 'Portuguese-Brazilian', native: 'Português (Brasil)' },
  { code: 'es-ES', name: 'Spanish-Castilian', native: 'Español (España)' },
  { code: 'es-MX', name: 'Spanish-Modern', native: 'Español (México)' },
  { code: 'ar', name: 'Arabic', native: 'العربية' }
];

const ViewQuestion: React.FC = () => {
  const { questionId: paramQuestionId } = useParams<{ questionId: string }>();
  const navigate = useNavigate();
  const [questionId, setQuestionId] = useState(paramQuestionId || '');
  const [question, setQuestion] = useState<Question | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [showTranslations, setShowTranslations] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null);
  const [editFormData, setEditFormData] = useState<{
    question_text: string;
    options: { [key: string]: string };
    explanation: string;
  } | null>(null);
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [retranslating, setRetranslating] = useState<string | null>(null);
  const [translationModel, setTranslationModel] = useState<string>('gemma3:27b');

  const fetchQuestion = async (id: string) => {
    if (!id || !id.trim()) {
      setError('Please enter a question ID');
      return;
    }

    setLoading(true);
    setError('');
    setQuestion(null);
    setExam(null);
    setTranslations([]);
    setShowTranslations(false);

    try {
      const response = await api.get(`/exams/questions/${id.trim()}`);
      setQuestion(response.data.question);
      setExam(response.data.exam);
      // Fetch translations
      fetchTranslations(id.trim());
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError(`Question with ID ${id} not found`);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch question');
      }
      setQuestion(null);
      setExam(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchTranslations = async (id: string) => {
    setLoadingTranslations(true);
    try {
      const response = await api.get(`/exams/questions/${id}/translations`);
      setTranslations(response.data.translations || []);
    } catch (err: any) {
      console.error('Failed to fetch translations:', err);
      // Don't show error, just log it
    } finally {
      setLoadingTranslations(false);
    }
  };

  const handleEditTranslation = (translation: Translation) => {
    setEditingTranslation(translation);
    setEditFormData({
      question_text: translation.question_text,
      options: { ...translation.options },
      explanation: translation.explanation || ''
    });
  };

  const handleSaveTranslation = async () => {
    if (!editingTranslation || !editFormData || !question) return;

    setSavingTranslation(true);
    try {
      await api.put(
        `/exams/questions/${question.id}/translations/${editingTranslation.language_code}`,
        editFormData
      );
      // Refresh translations
      await fetchTranslations(question.id.toString());
      setEditingTranslation(null);
      setEditFormData(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save translation');
    } finally {
      setSavingTranslation(false);
    }
  };

  const handleRetranslate = async (languageCode: string) => {
    if (!question) return;

    setRetranslating(languageCode);
    try {
      await api.post(`/exams/questions/${question.id}/retranslate`, {
        language: languageCode,
        model: translationModel
      });
      // Wait a bit then refresh translations
      setTimeout(() => {
        fetchTranslations(question.id.toString());
        setRetranslating(null);
      }, 2000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to trigger retranslation');
      setRetranslating(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchQuestion(questionId);
  };

  // Auto-fetch if questionId is in URL
  React.useEffect(() => {
    if (paramQuestionId) {
      setQuestionId(paramQuestionId);
      fetchQuestion(paramQuestionId);
    }
  }, [paramQuestionId]);

  const correctAnswers = question?.correct_answer_keys || [];
  const isMultipleAnswer = correctAnswers.length > 1;

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        {/* Header */}
        <div className="mb-5">
          <Link
            to="/admin/certifications"
            className="gp-back-link mb-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2 text-xs" />
            Back to Admin Panel
          </Link>
          
          <div className="flex items-center gap-2.5 mb-3">
            <span className="gp-icon-tile-lg">
              <FontAwesomeIcon icon={faQuestionCircle} className="text-lg" aria-hidden="true" />
            </span>
            <div>
              <h1 className="gp-page-title">View Question</h1>
              <p className="gp-page-subtitle">Search and view questions by ID</p>
            </div>
          </div>
        </div>

        {/* Search Form */}
        <div className="gp-card mb-5">
          <form onSubmit={handleSubmit} className="flex items-center gap-2.5">
            <div className="flex-1">
              <label htmlFor="questionId" className="block text-sm font-semibold mb-2">
                Question ID
              </label>
              <input
                id="questionId"
                type="number"
                value={questionId}
                onChange={(e) => setQuestionId(e.target.value)}
                className="input-field"
                placeholder="Enter question ID..."
                min="1"
              />
            </div>
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading || !questionId.trim()}
                className="gp-btn-primary flex items-center gap-2 px-6 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="text-xs" />
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="gp-alert-error mb-4" role="alert">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Question Display */}
        {question && (
          <div className="space-y-4">
            {/* Exam Info */}
            {exam && (
              <div className="gp-card gp-soft-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold mb-1">
                      {exam.name}
                    </h2>
                    {exam.description && (
                      <p className="text-sm gp-text-muted">{exam.description}</p>
                    )}
                  </div>
                  <Link
                    to={`/admin/questions/${exam.id}`}
                    className="gp-btn-ghost flex items-center gap-2 px-4 py-2 text-sm"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                    <span>Manage Questions</span>
                  </Link>
                </div>
              </div>
            )}

            {/* Question Card */}
            <div className="gp-card border-2">
              {/* Question Header */}
              <div className="flex items-start justify-between mb-4 pb-4 border-b-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold gp-text-muted">
                      ID: {question.id}
                    </span>
                    {question.topic && (
                      <>
                        <span className="gp-text-muted">•</span>
                        <span className="px-2 py-0.5 rounded text-xs">
                          {question.topic}
                        </span>
                      </>
                    )}
                    {question.difficulty && (
                      <>
                        <span className="gp-text-muted">•</span>
                        <span className="text-xs gp-text-muted">
                          {'★'.repeat(question.difficulty)}{'☆'.repeat(5 - question.difficulty)}
                        </span>
                      </>
                    )}
                    {question.is_free_tier && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold">
                        Free
                      </span>
                    )}
                    {question.ai_generated_explanation && (
                      <span className="px-2 py-0.5 gp-badge-ai rounded text-xs font-semibold flex items-center space-x-1">
                        <FontAwesomeIcon icon={faRobot} className="text-xs" />
                        <span>AI Generated</span>
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/admin/questions/${question.exam_id}`}
                  className="gp-btn-ghost flex items-center gap-2 px-3 py-2 text-xs"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                  <span>Edit</span>
                </Link>
              </div>

              {/* Question Text */}
              <div className="mb-5">
                <h3 className="text-lg font-semibold mb-4 leading-relaxed">
                  <MarkdownRenderer content={question.question_text} />
                </h3>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-5">
                <h4 className="text-sm font-semibold mb-3">
                  Options:
                </h4>
                {Object.entries(question.options).map(([key, value]) => {
                  const isCorrect = correctAnswers.includes(key);
                  return (
                    <div
                      key={key}
                      className="p-3 rounded-md transition-all"
                      style={
                        isCorrect
                          ? { background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)' }
                          : { border: '1px solid var(--gp-border)' }
                      }
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center font-bold text-sm"
                          style={
                            isCorrect
                              ? { background: 'var(--gp-success)', color: '#fff' }
                              : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink)' }
                          }
                        >
                          {key}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm">
                            <MarkdownRenderer content={value} />
                          </div>
                        </div>
                        {isCorrect && (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="mt-1 flex-shrink-0"
                            style={{ color: 'var(--gp-success)' }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Correct Answer */}
              <div
                className="mb-4 p-3 rounded-md"
                style={{ background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--gp-success)' }}>
                    Correct Answer{isMultipleAnswer ? 's' : ''}:
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {correctAnswers.map((key) => (
                    <span
                      key={key}
                      className="px-2.5 py-1 rounded-md text-sm font-semibold"
                      style={{ background: 'var(--gp-success)', color: '#fff' }}
                    >
                      {key}. {question.options[key] || key}
                    </span>
                  ))}
                </div>
              </div>

              {/* Explanation */}
              {question.explanation && (
                <div className="p-4 gp-soft-panel rounded-lg">
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={question.ai_generated_explanation ? faRobot : faInfoCircle}
                      className={`mt-0.5 flex-shrink-0 ${ question.ai_generated_explanation ? '' : '' }`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-semibold">
                          Explanation
                        </h4>
                        {question.ai_generated_explanation && (
                          <span className="px-2 py-0.5 gp-badge-ai rounded text-xs">
                            AI Generated
                          </span>
                        )}
                      </div>
                      <div className="text-sm leading-relaxed">
                        <MarkdownRenderer content={question.explanation} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="mt-6 pt-4 border-t">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs gp-text-muted">
                  <div>
                    <span className="font-semibold">Question ID:</span> {question.id}
                  </div>
                  <div>
                    <span className="font-semibold">Exam ID:</span> {question.exam_id}
                  </div>
                  <div>
                    <span className="font-semibold">Created:</span>{' '}
                    {new Date(question.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className="font-semibold">Free Tier:</span>{' '}
                    {question.is_free_tier ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>

            {/* Translations Section */}
            <div className="gp-card border-2">
              <div className="flex items-center justify-between mb-4 pb-4 border-b-2">
                <div className="flex items-center gap-2.5">
                  <FontAwesomeIcon icon={faLanguage} className="text-lg" />
                  <h2 className="text-lg font-bold">
                    Translations
                  </h2>
                  <span className="px-2 py-1 gp-text-muted rounded text-xs">
                    {translations.length} / {SUPPORTED_LANGUAGES.length}
                  </span>
                </div>
                <button
                  onClick={() => setShowTranslations(!showTranslations)}
                  className="gp-btn-ghost flex items-center gap-2 px-3 py-2 text-xs"
                >
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`text-xs transition-transform ${showTranslations ? 'rotate-180' : ''}`}
                  />
                  <span>{showTranslations ? 'Hide' : 'Show'} Translations</span>
                </button>
              </div>

              {showTranslations && (
                <div>
                  {loadingTranslations ? (
                    <div className="flex items-center justify-center py-8">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Language and Model Selectors */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-semibold mb-2">
                            Select Language:
                          </label>
                          <select
                            value={selectedLanguage}
                            onChange={(e) => {
                              setSelectedLanguage(e.target.value);
                              setEditingTranslation(null);
                              setEditFormData(null);
                            }}
                            className="input-field"
                          >
                            <option value="">-- Select a language --</option>
                            {SUPPORTED_LANGUAGES.map((lang) => {
                              const translation = translations.find(t => t.language_code === lang.code);
                              return (
                                <option key={lang.code} value={lang.code}>
                                  {lang.name} ({lang.native}) {translation ? '✓' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2">
                            Translation Model:
                          </label>
                          <select
                            value={translationModel}
                            onChange={(e) => setTranslationModel(e.target.value)}
                            className="input-field"
                          >
                            <option value="gemma3:27b">gemma3:27b (Recommended)</option>
                            <option value="llama3.2">llama3.2</option>
                            <option value="llama3.1">llama3.1</option>
                            <option value="mistral">mistral</option>
                            <option value="phi3">phi3</option>
                            <option value="qwen2.5">qwen2.5</option>
                            <option value="lauchacarro/qwen2.5-translator:latest">lauchacarro/qwen2.5-translator:latest</option>
                          </select>
                        </div>
                      </div>

                      {/* Translation Display/Edit */}
                      {selectedLanguage && (
                        <div className="border-2 rounded-lg p-4 gp-soft-panel">
                          {(() => {
                            const translation = translations.find(t => t.language_code === selectedLanguage);
                            const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage);
                            const isRTL = selectedLanguage === 'ar';

                            if (editingTranslation && editingTranslation.language_code === selectedLanguage && editFormData) {
                              // Edit Mode
                              return (
                                <div className={`space-y-3 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                  <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <h3 className="text-base font-bold">
                                      Editing: {langInfo?.name} ({langInfo?.native})
                                    </h3>
                                    <div className={`flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'}`}>
                                      <button
                                        onClick={handleSaveTranslation}
                                        disabled={savingTranslation}
                                        className={`gp-btn-primary flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'} px-3 py-2 text-xs disabled:opacity-50`}
                                      >
                                        <FontAwesomeIcon icon={savingTranslation ? faSpinner : faSave} className={`text-xs ${savingTranslation ? 'animate-spin' : ''}`} />
                                        <span>Save</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingTranslation(null);
                                          setEditFormData(null);
                                        }}
                                        className={`gp-btn-ghost flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'} px-3 py-2 text-xs`}
                                      >
                                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                        <span>Cancel</span>
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <label className={`block text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                      Question Text:
                                    </label>
                                    <textarea
                                      value={editFormData.question_text}
                                      onChange={(e) => setEditFormData({ ...editFormData, question_text: e.target.value })}
                                      className={`input-field min-h-[100px] ${isRTL ? 'text-right' : 'text-left'}`}
                                      dir={isRTL ? 'rtl' : 'ltr'}
                                      rows={4}
                                    />
                                  </div>

                                  <div>
                                    <label className={`block text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                      Options:
                                    </label>
                                    <div className="space-y-2">
                                      {Object.entries(editFormData.options).map(([key, value]) => (
                                        <div key={key} className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse gap-2' : 'gap-2'}`}>
                                          <span className={`w-8 text-sm font-semibold ${isRTL ? 'ml-2' : 'mr-2'}`}>{key}:</span>
                                          <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => setEditFormData({
                                              ...editFormData,
                                              options: { ...editFormData.options, [key]: e.target.value }
                                            })}
                                            className={`flex-1 px-4 py-2 border rounded-lg text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div>
                                    <label className={`block text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                      Explanation:
                                    </label>
                                    <textarea
                                      value={editFormData.explanation}
                                      onChange={(e) => setEditFormData({ ...editFormData, explanation: e.target.value })}
                                      className={`input-field min-h-[80px] ${isRTL ? 'text-right' : 'text-left'}`}
                                      dir={isRTL ? 'rtl' : 'ltr'}
                                      rows={3}
                                    />
                                  </div>
                                </div>
                              );
                            } else if (translation) {
                              // View Mode with existing translation
                              return (
                                <div className={`space-y-3 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                  <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div>
                                      <h3 className={`text-base font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {langInfo?.name} ({langInfo?.native})
                                      </h3>
                                      <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse gap-2' : 'gap-2'} mt-1`}>
                                        {translation.ai_generated && (
                                          <span className={`px-2 py-0.5 gp-badge-ai rounded text-xs flex items-center ${isRTL ? 'space-x-reverse space-x-1' : 'space-x-1'}`}>
                                            <FontAwesomeIcon icon={faRobot} className="text-xs" />
                                            <span>AI Generated</span>
                                          </span>
                                        )}
                                        <span className="text-xs gp-text-muted">
                                          Updated: {new Date(translation.updated_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                    <div className={`flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'}`}>
                                      <button
                                        onClick={() => handleEditTranslation(translation)}
                                        className={`gp-btn-ghost flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'} px-3 py-2 text-xs`}
                                      >
                                        <FontAwesomeIcon icon={faEdit} className="text-xs" />
                                        <span>Edit</span>
                                      </button>
                                      <button
                                        onClick={() => handleRetranslate(selectedLanguage)}
                                        disabled={retranslating === selectedLanguage}
                                        className={`gp-btn-ghost flex items-center ${isRTL ? 'space-x-reverse gap-2' : 'gap-2'} px-3 py-2 text-xs disabled:opacity-50`}
                                      >
                                        <FontAwesomeIcon 
                                          icon={retranslating === selectedLanguage ? faSpinner : faRedo} 
                                          className={`text-xs ${retranslating === selectedLanguage ? 'animate-spin' : ''}`}
                                        />
                                        <span>Re-translate</span>
                                      </button>
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className={`text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>Question Text:</h4>
                                    <div className={`p-3 rounded-lg border ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                      <MarkdownRenderer content={translation.question_text} />
                                    </div>
                                  </div>

                                  <div>
                                    <h4 className={`text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>Options:</h4>
                                    <div className="space-y-2">
                                      {Object.entries(translation.options).map(([key, value]) => (
                                        <div key={key} className={`p-3 rounded-lg border ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                          <span className={`font-semibold ${isRTL ? 'ml-2' : 'mr-2'}`}>{key}:</span>
                                          <MarkdownRenderer content={value} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {translation.explanation && (
                                    <div>
                                      <h4 className={`text-sm font-semibold mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>Explanation:</h4>
                                      <div className={`p-3 rounded-lg border ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                                        <MarkdownRenderer content={translation.explanation} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              // No translation exists
                              return (
                                <div className="text-center py-6">
                                  <FontAwesomeIcon icon={faGlobe} className="text-4xl gp-text-muted mb-3" />
                                  <p className="text-sm gp-text-muted mb-4">
                                    No translation exists for {langInfo?.name} ({langInfo?.native})
                                  </p>
                                  <button
                                    onClick={() => handleRetranslate(selectedLanguage)}
                                    disabled={retranslating === selectedLanguage}
                                    className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm mx-auto disabled:opacity-50"
                                  >
                                    <FontAwesomeIcon 
                                      icon={retranslating === selectedLanguage ? faSpinner : faRobot} 
                                      className={`text-xs ${retranslating === selectedLanguage ? 'animate-spin' : ''}`}
                                    />
                                    <span>Generate Translation</span>
                                  </button>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}

                      {/* Translations List */}
                      {!selectedLanguage && translations.length > 0 && (
                        <div>
                          <h3 className="text-sm font-semibold mb-3">
                            Available Translations:
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                            {translations.map((translation) => {
                              const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === translation.language_code);
                              return (
                                <button
                                  key={translation.language_code}
                                  onClick={() => setSelectedLanguage(translation.language_code)}
                                  className="p-3 rounded-lg border-2 transition-all text-left"
                                >
                                  <div className="font-semibold text-sm">
                                    {langInfo?.name}
                                  </div>
                                  <div className="text-xs gp-text-muted mt-1">
                                    {langInfo?.native}
                                  </div>
                                  {translation.ai_generated && (
                                    <div className="mt-2">
                                      <span className="px-1.5 py-0.5 gp-badge-ai rounded text-xs">
                                        AI
                                      </span>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewQuestion;

